/**
 * The week-completion moment — the bigger celebration.
 *
 * Estia is a weekly product: the week is the climax, not the individual recipe.
 * Finishing a whole schedule used to produce nothing at all, and the only
 * prompt to build another one sat on a dashboard card the user had to go and
 * find. So the "build next week" call to action lives here, in the moment the
 * week actually ends.
 *
 * The Cookbook offer sits here too, as a secondary action rather than a modal
 * that covers this one. The Cookbook is a curated shelf, not a history: nothing
 * is saved by completing a week, and the button is the user saying they liked
 * it. It is also the only place in the product where "save this week" appears
 * at the moment the week means something.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";

import { RED, GREEN, GREEN_LIGHT, BORDER, MUTED } from "assets/theme/brandColors";

const GREEN_TEXT = "#2e7d32";
const INK = "#222";

const MAX_TECHNIQUE_CHIPS = 6;

function Stat({ value, label, detail }) {
  return (
    <Box sx={{ textAlign: "center", flex: 1, minWidth: 72 }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: RED, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          color: MUTED,
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
      {detail && <Typography sx={{ fontSize: 10, color: MUTED, mt: 0.15 }}>{detail}</Typography>}
    </Box>
  );
}

Stat.propTypes = {
  value: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  /** Optional second line under the label — used to break a total into parts. */
  detail: PropTypes.string,
};

Stat.defaultProps = {
  detail: null,
};

function WeekCompleteMoment({ summary, saved, onSave, onClose }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [savedNow, setSavedNow] = useState(saved);

  useEffect(() => setSavedNow(saved), [saved, summary]);

  if (!summary) return null;

  const recipes = summary.recipes || [];
  // The week's whole haul: cooking XP plus the goals that cooking completed.
  // Showing recipe XP alone read as 350 to a first-week tester whose total was
  // 550 — the headline contradicting the only other number they could see.
  // `total_xp` falls back to the sum for a payload from an older server.
  const recipeXp = summary.recipe_xp ?? 0;
  const goalXp = summary.goal_xp ?? 0;
  const weekXp = summary.total_xp ?? recipeXp + goalXp;
  const techniques = summary.techniques || [];
  const shownTechniques = techniques.slice(0, MAX_TECHNIQUE_CHIPS);
  const hiddenTechniques = techniques.length - shownTechniques.length;

  const handleSave = async () => {
    if (saving || savedNow) return;
    setSaving(true);
    try {
      const ok = await onSave();
      if (ok !== false) setSavedNow(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      aria-labelledby="week-complete-title"
    >
      <Box sx={{ bgcolor: RED, color: "#fff", px: 3, pt: 3, pb: 2.5, textAlign: "center" }}>
        <Typography sx={{ fontSize: 34, lineHeight: 1 }}>🏆</Typography>
        <Typography
          id="week-complete-title"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            mt: 1,
          }}
        >
          Week complete
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5, lineHeight: 1.25 }}>
          {summary.title || "Your week"}
        </Typography>
        <Typography sx={{ fontSize: 13, mt: 0.75, opacity: 0.95 }}>
          You cooked every recipe you planned.
        </Typography>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
          <Stat value={summary.recipes_cooked ?? recipes.length} label="Recipes" />
          <Stat
            value={`${weekXp}`}
            label="XP this week"
            detail={goalXp > 0 ? `${recipeXp} cooking · ${goalXp} goals` : null}
          />
          <Stat value={summary.streak ?? 0} label="Week streak" />
        </Box>

        {recipes.length > 0 && (
          <Box
            sx={{
              mt: 2.25,
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              px: 1.75,
              py: 1.25,
              display: "flex",
              flexDirection: "column",
              gap: 0.4,
            }}
          >
            {recipes.map((r) => (
              <Box key={r.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ color: GREEN_TEXT, fontSize: 13, fontWeight: 800 }}>
                  ✓
                </Box>
                <Typography sx={{ fontSize: 13, color: INK, flex: 1, minWidth: 0 }}>
                  {r.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>
                  {r.xp} XP
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {shownTechniques.length > 0 && (
          <Box sx={{ mt: 2.25 }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: MUTED,
                mb: 0.75,
              }}
            >
              Skills you practised this week
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {shownTechniques.map((t) => (
                <Box
                  key={t.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    bgcolor: t.tier_up ? GREEN_LIGHT : "transparent",
                    border: `1px solid ${t.tier_up ? GREEN : BORDER}`,
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK }}>
                    {t.display_name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: t.tier_up ? GREEN_TEXT : MUTED }}>
                    ×{t.reps_gained}
                  </Typography>
                </Box>
              ))}
              {hiddenTechniques > 0 && (
                <Typography sx={{ fontSize: 12, color: MUTED, alignSelf: "center" }}>
                  +{hiddenTechniques} more
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* The point of the moment: another week. */}
        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate("/schedule-builder")}
          sx={{
            mt: 2.5,
            fontSize: 15,
            fontWeight: 700,
            py: 1.25,
            textTransform: "none",
            bgcolor: RED,
            color: "#fff",
            "&:hover": { bgcolor: RED, color: "#fff", filter: "brightness(0.9)" },
          }}
        >
          Build next week
        </Button>

        <Box sx={{ display: "flex", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
          <Button
            fullWidth={false}
            onClick={handleSave}
            disabled={saving || savedNow}
            sx={{
              flex: 1,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              border: `1px solid ${savedNow ? GREEN : BORDER}`,
              color: savedNow ? GREEN_TEXT : RED,
              bgcolor: savedNow ? GREEN_LIGHT : "transparent",
              "&.Mui-disabled": { color: GREEN_TEXT, bgcolor: GREEN_LIGHT },
            }}
          >
            {savedNow ? "★ In your Cookbook" : "☆ Save schedule to Cookbook"}
          </Button>
          <Button
            onClick={onClose}
            sx={{ flex: 1, fontSize: 13, textTransform: "none", color: MUTED }}
          >
            Not now
          </Button>
        </Box>
        <Typography sx={{ fontSize: 11, color: MUTED, textAlign: "center", mt: 1 }}>
          Your Cookbook keeps the schedules and recipes you liked — nothing is saved automatically.
        </Typography>
      </Box>
    </Dialog>
  );
}

WeekCompleteMoment.propTypes = {
  /** `schedule_summary` from the completion response, or null when the week
      is not finished. */
  summary: PropTypes.shape({
    title: PropTypes.string,
    recipes: PropTypes.array,
    recipes_cooked: PropTypes.number,
    recipe_xp: PropTypes.number,
    goal_xp: PropTypes.number,
    total_xp: PropTypes.number,
    techniques: PropTypes.array,
    streak: PropTypes.number,
  }),
  saved: PropTypes.bool,
  /** Saves the week. Resolving to `false` means the save failed. */
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

WeekCompleteMoment.defaultProps = {
  summary: null,
  saved: false,
};

export default WeekCompleteMoment;
